import { useContext, useState } from 'react'
import { Box, MenuItem, Modal, Button, Select, TextField, Tooltip, IconButton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TableComponent from '../../../shared/Table'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'
import { AddTemplateItems, LocationTemplateConfigs } from '../../../../utils/constants'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { SelectWithSearch } from '../../../shared/SearchSelect'
import { ILocationTemplateWithRegions } from '../../../../types/spectrum-manager.type'
import { RequestContext } from '../../../../contexts/request.context'
import { createLocationTemplate, deleteLocationTemplate } from '../../../../services/spectrum-manager.service'
import { useToast } from '../../../../contexts/toast.context'
import { AppConfigContext } from '../../../../contexts/app-config.context'
import { usePermission } from '../../../../hooks/usePermission'

import styles from './styles.module.scss'

export const LocationTemplates = () => {
  const [isAdding, setIsAdding] = useState(false)
  const [locationTemplate, setLocationTemplate] = useState<ILocationTemplateWithRegions | null>(null)

  const { showToast } = useToast()
  const { locationTemplatesWithRegions, setLocationTemplatesWithRegions } = useContext(SpectrumManagerContext)

  const { isLightMode } = useContext(AppConfigContext)
  const { stateRegions } = useContext(RequestContext)
  const hasWriteAccess = usePermission('write', 'locations')

  const handleTemplateChange = (key: string, value: string | number) => {
    const template = locationTemplate ? locationTemplate : ({} as ILocationTemplateWithRegions)

    setLocationTemplate({ ...template, [key]: value })
  }

  const handleRemoveLocation = async (id: number) => {
    const data = await deleteLocationTemplate(id)

    if (data.success) {
      setLocationTemplatesWithRegions(locationTemplatesWithRegions.filter((template) => template.id !== id))

      showToast('success', 'Template removed successfully')
    }
  }

  const onClose = () => {
    setIsAdding(false)
  }

  const handleTemplateCreate = async () => {
    if (locationTemplate) {
      const { longitude_dir, latitude_dir, ...templateData } = locationTemplate as any

      const lonDeg = Number(templateData.longitude_deg) || 0
      const latDeg = Number(templateData.latitude_deg) || 0
      templateData.longitude_deg = longitude_dir === 'W' ? -Math.abs(lonDeg) : Math.abs(lonDeg)
      templateData.latitude_deg = latitude_dir === 'S' ? -Math.abs(latDeg) : Math.abs(latDeg)

      const data = await createLocationTemplate(templateData)

      if (data.data) {
        setLocationTemplatesWithRegions([data.data, ...locationTemplatesWithRegions])
        showToast('success', 'Template created successfully')
        onClose()
      }
    }
  }

  return (
    <Box className={styles.locationTemplate}>
      <TableComponent
        isLightMode={isLightMode}
        handleCreate={() => setIsAdding(true)}
        data={locationTemplatesWithRegions}
        configs={LocationTemplateConfigs}
        handleRefetch={() => {}}
        handleDelete={handleRemoveLocation}
        hasWriteAccess={!!hasWriteAccess}
      />
      <Modal className={styles.modal} open={isAdding} onClose={onClose}>
        <Box className={styles.modalContent}>
          <Box className={styles.header}>
            <Typography data-testid="title" className={styles.title}>
              Add Location
            </Typography>
            <IconButton data-testid="closeButton" onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
          <Box className={styles.content}>
            <Box className={styles.addTemplate}>
              {(
                [
                  AddTemplateItems.slice(0, AddTemplateItems.length / 2),
                  AddTemplateItems.slice(AddTemplateItems.length / 2),
                ] as (typeof AddTemplateItems)[]
              ).map((half, halfIdx) => (
                <Box key={halfIdx} className={styles.subContainer}>
                  {half.map((item) => {
                    const dashIdx = item.column.indexOf(' – ')
                    const shortLabel = dashIdx !== -1 ? item.column.slice(0, dashIdx) : item.column
                    const tooltipText = dashIdx !== -1 ? item.column.slice(dashIdx + 3) : ''
                    return (
                      <Box key={item.column} className={styles.fieldContainer}>
                        <Typography className={styles.label}>
                          {shortLabel}
                          {tooltipText && (
                            <Tooltip title={tooltipText}>
                              <InfoOutlinedIcon sx={{ fontSize: '0.8rem', ml: 0.25, verticalAlign: 'super' }} />
                            </Tooltip>
                          )}
                          {item.required !== false && <span className={styles.asterisk}>*</span>}:
                        </Typography>
                        {item.key === 'state_region_type_id' && (
                          <SelectWithSearch
                            label="Regions"
                            width={200}
                            options={stateRegions.map((region) => ({
                              value: region.id,
                              label: region.name,
                            }))}
                            value={stateRegions
                              .map((region) => ({
                                value: region.id,
                                label: region.name,
                              }))
                              .find((r) => r.value === locationTemplate?.state_region_type_id)}
                            handleChange={(value) => handleTemplateChange('state_region_type_id', Number(value))}
                          />
                        )}
                        {item.key !== 'state_region_type_id' && (item as any).type === 'select' && (
                          <Select
                            value={(locationTemplate as any)?.[item.key] || ''}
                            displayEmpty
                            className={styles.field}
                            size="small"
                            onChange={(evt) => handleTemplateChange(item.key, evt.target.value)}
                          >
                            <MenuItem value="" disabled>
                              Pick a direction
                            </MenuItem>
                            {((item as any).options as string[]).map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                        {item.key !== 'state_region_type_id' && (item as any).type !== 'select' && (
                          <TextField
                            placeholder="Enter value"
                            className={styles.field}
                            onChange={(evt) => handleTemplateChange(item.key, evt.target.value)}
                          />
                        )}
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Box>
          </Box>
          <Box className={styles.actions}>
            <Button
              id="confirmButton"
              data-testid="confirmButton"
              className={styles.removeBtn}
              onClick={handleTemplateCreate}
            >
              Create
            </Button>
            <Button className={styles.cancelBtn} color="inherit" onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}
